import React from "react";
import { motion } from "framer-motion";
import { Briefcase, Calendar, MapPin, ChevronRight, Building2 } from "lucide-react";
import { EXPERIENCE } from "../data/portfolioData";

export default function Experience() {
  return (
    <section id="experience" className="py-20 relative overflow-hidden bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-900 text-xs font-mono font-bold shadow-sm"
          >
            <Briefcase size={15} /> WORK HISTORY & CAREER TIMELINE
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900"
          >
            Professional Software & <span className="gradient-text-indigo">DevOps Experience</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-800 text-base font-medium leading-relaxed"
          >
            Track record across full-stack software development, Drupal CMS customization, Python Django microservices, and cloud infrastructure management.
          </motion.p>
        </div>

        {/* Vertical Timeline */}
        <div className="mt-16 relative">
          <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-500 via-purple-500 to-cyan-500 -translate-x-1/2 rounded-full opacity-40 hidden sm:block" />

          <div className="space-y-12">
            {EXPERIENCE.map((item, index) => {
              const isEven = index % 2 === 0;

              return (
                <motion.div
                  key={item.company + index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className={`relative flex flex-col ${
                    isEven ? "lg:flex-row-reverse" : "lg:flex-row"
                  } items-center gap-8`}
                >
                  {/* Timeline Glowing Node */}
                  <div className="absolute left-4 lg:left-1/2 -translate-x-1/2 top-8 z-20 hidden sm:flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full bg-white border-2 border-indigo-600 flex items-center justify-center shadow-md">
                      <div className="w-2.5 h-2.5 rounded-full bg-indigo-600 animate-ping" />
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="w-full lg:w-1/2 pl-0 sm:pl-10 lg:pl-0">
                    <div className="p-8 rounded-3xl bg-white border border-slate-200 hover:border-indigo-500 transition-all duration-300 shadow-sm hover:shadow-md space-y-6 group">
                      {/* Header */}
                      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Building2 className="text-indigo-600 shrink-0" size={20} />
                            <h3 className="text-xl font-black text-slate-900">
                              {item.role}
                            </h3>
                          </div>
                          <div className="text-sm font-bold text-indigo-700 flex items-center gap-2">
                            <span>{item.company}</span>
                            <span>•</span>
                            <span className="text-slate-700 font-mono text-xs flex items-center gap-1">
                              <MapPin size={13} /> {item.location}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-bold bg-indigo-100 text-indigo-900 border border-indigo-200">
                            <Calendar size={13} /> {item.duration}
                          </span>
                          <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-800 border border-slate-200">
                            {item.status}
                          </span>
                        </div>
                      </div>

                      {/* Bullet Achievements */}
                      <ul className="space-y-3 text-sm text-slate-800 font-medium leading-relaxed">
                        {item.bullets.map((bullet, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <ChevronRight size={16} className="text-indigo-600 shrink-0 mt-1" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Tech Stack Pills */}
                      <div className="pt-4 border-t border-slate-200 flex flex-wrap gap-1.5">
                        {item.tech.map((t) => (
                          <span
                            key={t}
                            className="px-2.5 py-1 rounded-lg text-xs font-mono font-bold bg-slate-100 text-slate-800 border border-slate-200"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="hidden lg:block lg:w-1/2" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
